import { Typography } from '@up-riac/bespoke-ui';
import dayjs from 'dayjs';
import React, { Suspense, useContext } from 'react';
import { AuctionDate } from 'src/components/AuctionDate/AuctionDate';
import { HeaderMobileMenuHeader } from 'src/components/Header/components/HeaderMobileMenu/HeaderMobileMenuHeader';
import { HeaderMobileMenuNavItem } from 'src/components/Header/components/HeaderMobileMenu/HeaderMobileMenuNavItem';
import { useFetch } from 'src/lib/FetchContext';
import { GlobalDataContext } from 'src/lib/GlobalDataContext';
import { usePrefetchSuspense } from 'src/lib/usePrefetch';

const HeaderMobileMenuAuctionsPageSubpages = ({ localPage, setLocalPage, closeMenu }) => {
  const { fetch } = useFetch();
  const data = usePrefetchSuspense('header-mobile-menu-auctions-page-subpages-data', async () => {
    const upcomingAuctionsRequest = await fetch('/api/auction/upcomingAuctions');
    const upcoming = await upcomingAuctionsRequest.json();
    const pastAuctionsRequest = await fetch('/api/auction/pastAuctions');
    const past = (await pastAuctionsRequest.json()).slice(0, 3);
    return { upcoming, past };
  });

  return (
    <>
      {localPage === 'upcoming' && <>
        <HeaderMobileMenuHeader
          id='header-mobile-menu-upcoming-header'
          onBack={() => setLocalPage('main')}
          onPress={closeMenu}
          to='/gun-auctions#upcoming'
        >
          Upcoming Auctions
        </HeaderMobileMenuHeader>
        {data.read().upcoming.map((auction, index) => (
          <HeaderMobileMenuNavItem
            key={`header-mobile-menu-upcoming-item-${index}`}
            id={`header-mobile-menu-upcoming-item-${index}`}
            label={auction.Title}
            noArrow
            description={<>
              <AuctionDate
                omitIcon
                auction = {auction}
                startDateFormat = 'MMM Do'
                endDateFormat={
                  dayjs.tz(auction.StartDate, 'America/Chicago').format('MMM')
                  === dayjs.tz(auction.EndDate, 'America/Chicago').format('MMM')
                    ? 'Do, YYYY'
                    : 'MMM Do, YYYY'
                }
                omitTime
              /><br/>
              {(auction.LocCity && auction.LocState) && `${auction.LocCity}, ${auction.LocState}`}
            </>}
            to={auction.CatalogSearchLink ? `/catalog/${auction.AuctionID}` : `/gun-auctions/${auction.Slug}`}
            onPress={() => closeMenu()}
          />
        ))}
      </>}
      {localPage === 'past' && <>
        <HeaderMobileMenuHeader
          id='header-mobile-menu-past-header'
          onBack={() => setLocalPage('main')}
          onPress={closeMenu}
          to='/gun-auctions#past'
        >
          Past Auctions
        </HeaderMobileMenuHeader>
        {data.read().past.map((auction, index) => (
          <HeaderMobileMenuNavItem
            key={`header-mobile-menu-past-item-${index}`}
            id={`header-mobile-menu-past-item-${index}`}
            label={auction.Title}
            noArrow
            description={<>
              <AuctionDate
                omitIcon
                auction = {auction}
                startDateFormat = 'MMM Do'
                endDateFormat={
                  dayjs.tz(auction.StartDate, 'America/Chicago').format('MMM')
                  === dayjs.tz(auction.EndDate, 'America/Chicago').format('MMM')
                    ? 'Do, YYYY'
                    : 'MMM Do, YYYY'
                }
                omitTime
              /><br/>
              {(auction.LocCity && auction.LocState) && `${auction.LocCity}, ${auction.LocState}`}
            </>}
            to={`/catalog/${auction.AuctionID}`}
            onPress={() => closeMenu()}
          />
        ))}
        <HeaderMobileMenuNavItem
          noArrow
          id='header-mobile-menu-past-view-all-item'
          label='View All Past Auctions'
          to='/gun-auctions#past'
          onPress={() => closeMenu()}
        />
      </>}
    </>
  );
};

export const HeaderMobileMenuAuctionsPage = ({ setPage, closeMenu }: {closeMenu: () => void, setPage: React.Dispatch<React.SetStateAction<'main' | 'login' | 'auctions' | 'consignment' | 'latest' | 'company' | 'locations' | 'account' | 'account/profile' | 'account/settings' | 'account/buying'>>}) => {
  const [localPage, setLocalPage] = React.useState<'main' | 'upcoming' | 'past'>('main');
  const { upcomingAuctions } = useContext(GlobalDataContext);

  return (<>
    {localPage === 'main' && <>
      <HeaderMobileMenuHeader
        to='/gun-auctions'
        onPress={closeMenu}
        id='header-mobile-menu-auctions-page-header'
        onBack={() => setPage('main')}
      >
        Auctions
      </HeaderMobileMenuHeader>
      {upcomingAuctions.slice(0, 2).map((auction, index) => (
        <HeaderMobileMenuNavItem
          key={`header-mobile-menu-auction-page-upcoming-auction-${index}`}
          id={`header-mobile-menu-auction-page-upcoming-auction-${index}`}
          label={auction.Title}
          noArrow
          accent
          description={
            <AuctionDate
              omitIcon
              auction = {auction}
              startDateFormat = 'MMM Do'
              endDateFormat={dayjs.tz(auction.StartDate, 'America/Chicago').format('MMM') === dayjs.tz(auction.EndDate, 'America/Chicago').format('MMM') ? 'Do, YYYY' : 'MMM Do, YYYY'}
              omitTime
            />
          }
          to={`/catalog/${auction.AuctionID}`}
          onPress={() => closeMenu()}
        />
      ))}
      <HeaderMobileMenuNavItem onPress={() => setLocalPage('upcoming')} id='header-mobile-menu-auction-page-upcoming' label='Upcoming Auctions'/>
      <HeaderMobileMenuNavItem onPress={() => setLocalPage('past')} id='header-mobile-menu-auction-page-past' label='Past Auctions'/>
    </>}
    <Suspense fallback={<Typography color='text-light'>Loading...</Typography>}>
      <HeaderMobileMenuAuctionsPageSubpages closeMenu={closeMenu} localPage={localPage} setLocalPage={setLocalPage} />
    </Suspense>
  </>);
};
